import { createContext as reactCreateContext, createElement, useContext, useMemo } from 'react';
import { StoreApi } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { ExtractState, UseContextStore } from './generics';

/**
 * Creates a React context with a Zustand store and provides a provider and hooks to access the store and its API.
 *
 * @see https://github.com/pmndrs/zustand/blob/6f61b76fa096179248a91231789fd5bbc51a36e0/src/context.ts#L40
 *
 * @example
 *
 * ```ts
 * import { createContext } from '@spazious/dollhouse-zustand';
 *
 * export const storeApi = create({
 *   count: 0,
 *   increment: () => set((state) => ({ count: state.count + 1 })),
 *   decrement: () => set((state) => ({ count: state.count - 1 })),
 * });
 *
 * const {
 *   Provider: StoreProvider,
 *   useStore,
 *   useStoreApi,
 *   Context: StoreContext,
 * } = createContext(storeApi);
 *
 * export { StoreProvider, useStore, useStoreApi, StoreContext };
 * ```
 */
export const createContext = <S extends StoreApi<unknown>>(defaultStoreApi?: S | never) => {
  const ZustandContext = reactCreateContext<S | null>(defaultStoreApi ?? null);

  const Provider = ({
    store,
    children,
  }: typeof defaultStoreApi extends StoreApi<unknown>
    ? { store: S; children: React.ReactNode }
    : { store?: S; children: React.ReactNode }) => {
    const ctx = useContext(ZustandContext);
    return createElement(ZustandContext.Provider, { value: store ?? ctx ?? null }, children);
  };

  const useStore: UseContextStore<S> = <StateSlice = ExtractState<S>>(
    selector?: (state: ExtractState<S>) => StateSlice,
    equalityFn?: (a: StateSlice, b: StateSlice) => boolean
  ) => {
    const store = useContext(ZustandContext);

    if (!store) {
      throw new Error('Seems like you have not used zustand provider as an ancestor.');
    }
    return useStoreWithEqualityFn(
      store,
      selector as (state: ExtractState<S>) => StateSlice,
      equalityFn
    );
  };

  const useStoreApi = () => {
    const store = useContext(ZustandContext);
    if (!store) {
      throw new Error('Seems like you have not used zustand provider as an ancestor.');
    }
    return useMemo<S>(() => ({ ...store }), [store]);
  };

  return {
    Provider,
    useStore,
    useStoreApi,
    Context: ZustandContext,
  };
};
